import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import LandingPage from './pages/LandingPage';
import AuthCallback from './pages/AuthCallback';
import StashInventory from './pages/StashInventory';
import EditItemPage from './pages/EditItemPage';
import CraftCompanion from './pages/DesignAssistant';
import BillingPage from './pages/BillingPage';
import StashedAwayPage from './pages/StashedAwayPage';
import ReferralLandingPage from './pages/ReferralLandingPage';
import SharedDesignPage from './pages/SharedDesignPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import CookiePolicyPage from './pages/CookiePolicyPage';
import AdminOverview from './pages/AdminOverview';
import AdminUsers from './pages/AdminUsers';
import AdminYouTube from './pages/AdminYouTube';
import AdminCategories from './pages/AdminCategories';
import AdminOccasions from './pages/AdminOccasions';
import AdminServices from './pages/AdminServices';
import AdminBilling from './pages/AdminBilling';
import AdminPromoCodes from './pages/AdminPromoCodes';
import AdminBrands from './pages/AdminBrands';
import AdminFeedback from './pages/AdminFeedback';
import GoogleCallback from './pages/GoogleCallback';
import FacebookCallback from './pages/FacebookCallback';
import MyDesignsPage from './pages/MyDesignsPage';
import DesignDetailPage from './pages/DesignDetailPage';
import PublicDesignPage from './pages/PublicDesignPage';
import CommunityFeedPage from './pages/CommunityFeedPage';
import InspirationPage from './pages/InspirationPage';
import CreatePage from './pages/CreatePage';
import ExplorePage from './pages/ExplorePage';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CategoryProvider } from './context/CategoryContext';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import ImpersonationBanner from './components/ImpersonationBanner';
import './App.css';

function AppRouter() {
  const location = useLocation();
  
  // Check if we're in OAuth callback (session_id or session_token in hash)
  if (location.hash?.includes('session_id=') || location.hash?.includes('session_token=')) {
    console.log('AppRouter: Detected auth data in hash, rendering AuthCallback');
    return <AuthCallback />;
  }
  
  console.log('AppRouter: Rendering routes, path:', location.pathname);
  
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/join/:code" element={<ReferralLandingPage />} />
      <Route path="/shared/:shareId" element={<SharedDesignPage />} />
      <Route path="/d/:designId" element={<PublicDesignPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/cookies" element={<CookiePolicyPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/auth/google/callback" element={<GoogleCallback />} />
      <Route path="/auth/facebook/callback" element={<FacebookCallback />} />
      
      {/* Dashboard redirects to My Stash (new home) */}
      <Route path="/dashboard" element={<Navigate to="/stash" replace />} />
      
      {/* Main 3-Tab Navigation */}
      <Route path="/stash" element={
        <ProtectedRoute>
          <StashInventory />
        </ProtectedRoute>
      } />
      <Route path="/stash/edit/:itemId" element={
        <ProtectedRoute>
          <EditItemPage />
        </ProtectedRoute>
      } />
      <Route path="/create" element={
        <ProtectedRoute>
          <CreatePage />
        </ProtectedRoute>
      } />
      <Route path="/explore" element={
        <ProtectedRoute>
          <ExplorePage />
        </ProtectedRoute>
      } />
      
      {/* Legacy route - redirect to Create */}
      <Route path="/my-designs" element={
        <ProtectedRoute>
          <CreatePage />
        </ProtectedRoute>
      } />
      <Route path="/my-designs/:designId" element={
        <ProtectedRoute>
          <DesignDetailPage />
        </ProtectedRoute>
      } />
      
      {/* Legacy route - redirect to Explore */}
      <Route path="/community" element={
        <ProtectedRoute>
          <ExplorePage />
        </ProtectedRoute>
      } />
      <Route path="/designs/:designId" element={
        <ProtectedRoute>
          <DesignDetailPage fromCommunity />
        </ProtectedRoute>
      } />
      
      {/* Hidden but accessible routes (will be refactored later) */}
      <Route path="/design" element={
        <ProtectedRoute>
          <CraftCompanion />
        </ProtectedRoute>
      } />
      <Route path="/inspiration" element={
        <ProtectedRoute>
          <InspirationPage />
        </ProtectedRoute>
      } />
      
      {/* Billing & Stashed Away */}
      <Route path="/billing" element={
        <ProtectedRoute>
          <BillingPage />
        </ProtectedRoute>
      } />
      <Route path="/stash/stashed-away" element={
        <ProtectedRoute>
          <StashedAwayPage />
        </ProtectedRoute>
      } />
      
      {/* Admin Routes */}
      <Route path="/admin" element={
        <AdminRoute>
          <AdminOverview />
        </AdminRoute>
      } />
      <Route path="/admin/users" element={
        <AdminRoute>
          <AdminUsers />
        </AdminRoute>
      } />
      <Route path="/admin/youtube" element={
        <AdminRoute>
          <AdminYouTube />
        </AdminRoute>
      } />
      <Route path="/admin/categories" element={
        <AdminRoute>
          <AdminCategories />
        </AdminRoute>
      } />
      <Route path="/admin/occasions" element={
        <AdminRoute>
          <AdminOccasions />
        </AdminRoute>
      } />
      <Route path="/admin/services" element={
        <AdminRoute>
          <AdminServices />
        </AdminRoute>
      } />
      <Route path="/admin/billing" element={
        <AdminRoute>
          <AdminBilling />
        </AdminRoute>
      } />
      <Route path="/admin/promo-codes" element={
        <AdminRoute>
          <AdminPromoCodes />
        </AdminRoute>
      } />
      <Route path="/admin/brands" element={
        <AdminRoute>
          <AdminBrands />
        </AdminRoute>
      } />
      <Route path="/admin/feedback" element={
        <AdminRoute>
          <AdminFeedback />
        </AdminRoute>
      } />
      
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CategoryProvider>
          <ImpersonationBanner />
          <AppRouter />
        </CategoryProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
